<template>
  <v-tooltip
    location="top"
    :disabled="!showUpselling"
  >
    <template #activator="{ props: tooltipProps }">
      <div
        v-bind="tooltipProps"
        class="w-100"
      >
        <v-select
          v-model="targetLink"
          :items="targetLinks"
          variant="outlined"
          hide-details
          density="compact"
          :label="t('buttons.content-creator.target-link')"
          class="pointer"
          item-title="text"
          item-value="value"
          :menu-props="{ closeOnContentClick: false }"
          v-model:menu="isTargetLinkMenuOpen"
          :disabled="isLoading || showUpselling"
          @update:model-value="handleTargetLinkChange"
          @click="handleTargetLinkClick"
          :error="!!individualLinkError"
          :error-messages="individualLinkError"
          data-product-fruits="content-creator-target-link"
        >
          <template #item="{ item, props: itemProps }">
            <v-tooltip
              v-if="!item.raw.hasInputField"
              :disabled="!item.raw?.disabled"
            >
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-list-item
                    v-bind="itemProps"
                    :disabled="item.raw.disabled"
                    :title="item.raw.text"
                  />
                </div>
              </template>
              <span>{{ item.raw.disableMessage }}</span>
            </v-tooltip>

            <div
              v-else
              class="px-4 py-2"
            >
              <div class="text-body-2 mb-2">
                {{ item.raw.text }}
              </div>

              <v-text-field
                v-model="individualLink"
                variant="outlined"
                density="compact"
                hide-details
                :label="t('content-creator.target-links.8')"
                placeholder="https://..."
                :error="!!individualLinkError"
                :error-messages="individualLinkError"
                @focus="handleIndividualLinkFocus(item.raw.value)"
                @mousedown.stop
                @click.stop
                @keydown.stop
                @keydown.enter.stop.prevent="confirmIndividualLink"
              />
            </div>
          </template>

          <template #selection="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  class="text-truncate text-no-wrap"
                  style="
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    min-width: 0;
                    max-width: 100%;
                  "
                  v-bind="props"
                >
                  <span v-if="item?.raw?.hasInputField && individualLink">
                    {{ individualLink }}
                  </span>
                  <span v-else>
                    {{ item?.raw?.text }}
                  </span>
                </div>
              </template>
              <span>
                <span v-if="item?.raw?.hasInputField && individualLink">
                  {{ individualLink }}
                </span>
                <span v-else>
                  {{ item?.raw?.text }}
                </span>
              </span>
            </v-tooltip>
          </template>
        </v-select>
      </div>
    </template>
    <span>
      <div>
        {{ t('alerts.upselling.upgrade.2') }}
      </div>
    </span>
  </v-tooltip>
</template>
<script setup lang="ts">
import { computed, inject, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'
import { isValidUrl } from '@/lib/validation'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType'
import { TargetLink, TargetLinkType } from '@/modules/contentCreator/creator/enums/targetLink'
import { TrackingPlugin } from '@/plugins/tracking'
const $tracking = inject<TrackingPlugin>('tracking')

interface Props {
  enabledTargetLinks?: boolean
  isLoading?: boolean
  showUpselling?: boolean
  isCustomTopic?: boolean
  hasGoogleReview?: boolean
  kind?: String
}

const props = withDefaults(defineProps<Props>(), {
  enabledTargetLinks: true,
  isLoading: false,
  showUpselling: false,
  hasGoogleReview: false,
  isCustomTopic: false,
  kind: null,
})

const { t } = useI18n()
const route = useRoute()
const { isFeatureActive } = useFeature()

const emit = defineEmits(['disable-generate-button', 'individual-link', 'target-link'])
const isMasterView = computed(() => route.path.startsWith('/master'))
const targetLink = ref<TargetLinkType>(
  props.kind === PostType.whatsappStatus ? TargetLink.noLink : TargetLink.landingpage
)
const individualLink = ref('')
const linkValidationTimeout = ref(null)
const individualLinkError = ref('')
const isTargetLinkMenuOpen = ref(false)

const hasIndividualLinkError = computed(() => {
  return individualLinkError.value?.length > 0
})

const hasIndividualLinkValue = computed(() => {
  return individualLink.value?.length > 0
})

const disableGenerateButton = computed(() => {
  return (
    props.enabledTargetLinks &&
    (!targetLink.value ||
      (targetLink.value === TargetLink.individualLink &&
        (!hasIndividualLinkValue.value || hasIndividualLinkError.value)))
  )
})

const confirmIndividualLink = () => {
  clearTimeout(linkValidationTimeout.value)
  const formattedLink = formatUrl(individualLink.value)
  individualLink.value = formattedLink
  validateIndividualLink(formattedLink)
  if (!individualLinkError.value) {
    emit('individual-link', formattedLink)
    emit('disable-generate-button', disableGenerateButton.value)

    isTargetLinkMenuOpen.value = false
  }
}

const formatUrl = (value = '') => {
  const trimmedValue = value.trim()

  if (!trimmedValue) return ''

  let formattedValue = trimmedValue

  // Add protocol if missing
  if (!/^https?:\/\//i.test(formattedValue)) {
    formattedValue = `https://${formattedValue}`
  }

  try {
    const url = new URL(formattedValue)

    // Add .com if hostname has no extension
    if (!url.hostname.includes('.')) {
      url.hostname = `${url.hostname}.com`
    }

    return url.toString()
  } catch {
    return formattedValue
  }
}

watch(targetLink, (newValue) => {
  if (newValue !== TargetLink.individualLink) {
    individualLink.value = ''
    individualLinkError.value = ''
  }
  emit('target-link', targetLink.value)
  emit('disable-generate-button', disableGenerateButton.value)
})

watch(
  () => props.kind,
  (newValue) => {
    if (newValue === PostType.whatsappStatus) {
      targetLink.value = TargetLink.noLink
      return
    }

    if (targetLink.value === TargetLink.noLink) {
      targetLink.value = TargetLink.landingpage
    }

    if (targetLink.value === TargetLink.googleReview && newValue === PostType.newsletter) {
      targetLink.value = TargetLink.landingpage
    }
  },
  { immediate: true }
)

watch(isTargetLinkMenuOpen, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen && targetLink.value === TargetLink.individualLink) {
    confirmIndividualLink()
  }
})

watch(individualLink, (newValue) => {
  clearTimeout(linkValidationTimeout.value)

  linkValidationTimeout.value = setTimeout(() => {
    const formattedLink = formatUrl(newValue)
    validateIndividualLink(formattedLink)
    emit('individual-link', formattedLink)
    emit('disable-generate-button', disableGenerateButton.value)
  }, 400)
})

const targetLinks = computed(() => {
  const links = [
    {
      text: isMasterView.value
        ? t('content-creator.target-links.1')
        : t('content-creator.target-links.7'),
      value: TargetLink.customerMainWebsite,
      hasInputField: false,
    },
    {
      text: t('content-creator.target-links.2'),
      value: TargetLink.officeWebsite,
      hasInputField: false,
    },
    {
      text: t('content-creator.target-links.3'),
      value: TargetLink.landingpagePerOffice,
      hasInputField: false,
    },
    {
      text: isMasterView.value
        ? t('content-creator.target-links.4')
        : t('content-creator.target-links.6'),
      value: TargetLink.landingpage,
      hasInputField: false,
    },
    isFeatureActive(featureNames.GOOGLE_REVIEW_TARGET_LINK)
      ? {
          text: t('content-creator.target-links.google-review'),
          value: TargetLink.googleReview,
          disabled:
            props.kind === PostType.newsletter || (!props.hasGoogleReview && !isMasterView.value),
          disableMessage:
            props.kind === PostType.newsletter
              ? t('content-creator.target-link.google-review-disabled-newsletter')
              : t('content-creator.target-link.google-review-disabled'),
          hasInputField: false,
        }
      : null,
    {
      text: t('content-creator.target-links.5'),
      value: TargetLink.individualLink,
      hasInputField: true,
    },
  ].filter(Boolean)
  if (props.kind !== PostType.newsletter) {
    links.unshift({
      text: t('content-creator.target-links.0'),
      // @ts-ignore
      value: TargetLink.noLink,
      hasInputField: false,
    })
  }

  return links.filter((link) => {
    if (!isMasterView.value) {
      return (
        [
          TargetLink.noLink,
          TargetLink.customerMainWebsite,
          TargetLink.landingpage,
          TargetLink.googleReview,
          TargetLink.individualLink,
        ] as readonly TargetLinkType[]
      ).includes(link.value)
    }
    return true
  })
})

const handleTargetLinkChange = () => {
  $tracking.event('Content Creator', 'Selected', 'Target Link', targetLink.value)
  $tracking.heapEvent('link_selected')
}

// The individual-link option has no clickable list item of its own — it's
// selected by focusing its embedded text field, which bypasses the v-select's
// own update:model-value emission (and so handleTargetLinkChange).
const handleIndividualLinkFocus = (value: TargetLinkType) => {
  targetLink.value = value
  handleTargetLinkChange()
}

const handleTargetLinkClick = () => {
  $tracking.event('Content Creator', 'Clicked', 'Target Link')
}

const validateIndividualLink = (value) => {
  if (!value || targetLink.value !== TargetLink.individualLink) {
    individualLinkError.value = ''
    return
  }

  if (/\s/.test(value)) {
    individualLinkError.value = t('content-creator.target-links.validation-error.1')
    return
  }

  if (!isValidUrl(value)) {
    individualLinkError.value = t('content-creator.target-links.validation-error.2')
    return
  }

  individualLinkError.value = ''
}

onUnmounted(() => {
  clearTimeout(linkValidationTimeout.value)
})
</script>
